import React, { useEffect, useState } from "react";
import dayjs from "dayjs";
import DOMPurify from "dompurify";
import { Button, Card, Input, Select, Space, Table, Tag, Tooltip, Typography, message } from "antd";
import { EyeOutlined, SafetyCertificateOutlined, SearchOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import paths from "routes/paths";
import olreadiaService from "services/olreadiaService";
import WidgetSectionTitle from "widgets/WidgetSectionTitle";

const { Text } = Typography;
const PAGE_SIZE = 10;

const plainText = (value) =>
  DOMPurify.sanitize(String(value || ""), { ALLOWED_TAGS: [], ALLOWED_ATTR: [] }).trim();

const profileTitle = (record) =>
  plainText(record?.profileName || record?.sectorName) || "Profession profile";

export default function ProfessionProfilesPage() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [visibility, setVisibility] = useState();
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedQuery(query.trim());
      setPage(1);
    }, 350);
    return () => window.clearTimeout(timer);
  }, [query]);

  useEffect(() => {
    let active = true;
    const loadProfiles = async () => {
      setLoading(true);
      try {
        const response = await olreadiaService.professionProfiles.listAdmin({
          q: debouncedQuery || undefined,
          visibility,
          page: page - 1,
          size: PAGE_SIZE,
        });
        if (!active) return;
        setRows(Array.isArray(response?.content) ? response.content : []);
        setTotal(Number(response?.totalElements) || 0);
      } catch (error) {
        if (active) {
          setRows([]);
          setTotal(0);
          message.error(error?.response?.data?.message || "Failed to load profession profiles");
        }
      } finally {
        if (active) setLoading(false);
      }
    };
    loadProfiles();
    return () => {
      active = false;
    };
  }, [debouncedQuery, page, visibility]);

  const columns = [
    {
      title: "Professional",
      key: "person",
      width: 220,
      render: (_, record) => <Text strong>{record.userFullName || "Unnamed professional"}</Text>,
    },
    {
      title: "Profile",
      key: "profile",
      render: (_, record) => (
        <Tooltip title={profileTitle(record)}>
          <Text strong ellipsis style={{ maxWidth: 520 }}>
            {profileTitle(record)}
          </Text>
        </Tooltip>
      ),
    },
    {
      title: "Visibility",
      dataIndex: "visibility",
      width: 120,
      render: (value) => <Tag color={value === "PUBLIC" ? "green" : "default"}>{value || "UNKNOWN"}</Tag>,
    },
    {
      title: "Updated",
      dataIndex: "updatedAt",
      width: 145,
      render: (value) => (value ? dayjs(value).format("MMM D, YYYY") : "—"),
    },
    {
      title: "Actions",
      key: "actions",
      width: 104,
      align: "right",
      render: (_, record) => (
        <Space size={4}>
          <Tooltip title="View profile">
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={(event) => {
                event.stopPropagation();
                navigate(paths.helperProfessionProfileDetails(record.id), { state: { profile: record } });
              }}
            />
          </Tooltip>
          <Tooltip title="Background checks">
            <Button
              type="text"
              icon={<SafetyCertificateOutlined />}
              onClick={(event) => {
                event.stopPropagation();
                navigate(
                  `${paths.helperProfessionBackgroundChecks}?profileId=${record.id}`,
                  { state: { profile: record } },
                );
              }}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <div className="container-fluid mt-4" style={{ maxWidth: 1180, margin: "0 auto" }}>
      <WidgetSectionTitle
        icon="briefcase"
        transform="shrink-2"
        title="Profession Profiles"
        subtitle="Search and support profession profiles across all users."
        className="mb-3"
      />
      <Card styles={{ body: { padding: 16 } }} style={{ borderRadius: 14 }}>
        <Space wrap size={10} style={{ marginBottom: 14, width: "100%" }}>
          <Input
            allowClear
            prefix={<SearchOutlined />}
            placeholder="Search person, email, phone, or profile"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            style={{ width: 360, maxWidth: "100%" }}
          />
          <Select
            allowClear
            placeholder="All visibility"
            value={visibility}
            onChange={(value) => {
              setVisibility(value);
              setPage(1);
            }}
            options={[
              { value: "PUBLIC", label: "Public" },
              { value: "PRIVATE", label: "Private" },
              { value: "PROTECTED", label: "Protected" },
            ]}
            style={{ width: 160 }}
          />
          <Text type="secondary">{total} profiles</Text>
        </Space>
        <Table
          rowKey="id"
          columns={columns}
          dataSource={rows}
          loading={loading}
          size="small"
          onRow={(record) => ({
            onClick: () =>
              navigate(paths.helperProfessionProfileDetails(record.id), { state: { profile: record } }),
            style: { cursor: "pointer" },
          })}
          scroll={{ x: 820 }}
          pagination={{
            current: page,
            pageSize: PAGE_SIZE,
            total,
            showSizeChanger: false,
            onChange: setPage,
          }}
        />
      </Card>
    </div>
  );
}
